import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  Users,
  Calendar,
  Image,
  FolderOpen,
  StickyNote,
  ListChecks,
  MessageCircle,
  Video,
  Cloud,
} from 'lucide-react';

interface App {
  id: string;
  name: string;
  icon: any;
  color: string;
  badge?: string | number;
}

interface FrontDeskProps {
  onAppLaunch: (appId: string) => void;
}

export function FrontDesk({ onAppLaunch }: FrontDeskProps) {
  const [user] = useState({
    name: 'hxa.agency',
    email: 'user@wehexa.net',
    plan: 'WorkOs Pro',
  });

  const apps: App[] = [
    { id: 'mail', name: 'Mail', icon: Mail, color: '#007AFF' },
    { id: 'contacts', name: 'Contacts', icon: Users, color: '#8E8E93' },
    { id: 'calendar', name: 'Calendar', icon: Calendar, color: '#FF3B30', badge: '3' },
    { id: 'photos', name: 'Photos', icon: Image, color: '#FF9500' },
    { id: 'icloud', name: 'iCloud', icon: Cloud, color: '#007AFF' },
    { id: 'notes', name: 'Notes', icon: StickyNote, color: '#FFCC00' },
    { id: 'reminders', name: 'Reminders', icon: ListChecks, color: '#FF3B30', badge: '5' },
    { id: 'messages', name: 'Messages', icon: MessageCircle, color: '#34C759', badge: '12' },
    { id: 'calls', name: 'Calls', icon: Video, color: '#3390EC' },
  ];

  return (
    <div className="min-h-screen bg-macos-gray-100 dark:bg-macos-gray-950 relative overflow-hidden">
      {/* macOS-style background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-macos-blue/5 via-transparent to-macos-purple/5 dark:from-macos-blue/10 dark:to-macos-purple/10" />
      </div>

      <div className="relative z-10 px-4 sm:px-6 pt-8 sm:pt-16 pb-24 sm:pb-32 max-w-[1200px] mx-auto">
        {/* Profile Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="window-macos glass-macos bg-white/80 dark:bg-macos-gray-900/80 p-6 mb-6"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-macos-blue to-macos-purple flex items-center justify-center text-white text-2xl font-bold shadow-macos">
              {user.name.charAt(0)}
            </div>
            <div>
              <h2 className="text-xl font-bold text-macos-gray-950 dark:text-white">{user.name}</h2>
              <p className="text-macos-gray-600 dark:text-macos-gray-400 text-sm">{user.email}</p>
              <span className="inline-block mt-1 px-3 py-1 bg-macos-blue/10 dark:bg-macos-blue/20 rounded-full text-macos-blue text-xs font-medium">
                {user.plan}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Apps Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{apps.map((app, index) => {
            const Icon = app.icon;
            return (
              <motion.button
                key={app.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onAppLaunch(app.id)}
                className="window-macos glass-macos bg-white/60 dark:bg-macos-gray-900/60 p-6 hover:bg-white/80 dark:hover:bg-macos-gray-900/80 transition-all relative group"
              >
                {app.badge && (
                  <div className="absolute top-3 right-3 bg-macos-red text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-macos">
                    {app.badge}
                  </div>
                )}
                <div
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-macos-lg flex items-center justify-center mb-3 transition-transform group-hover:scale-110 shadow-macos"
                  style={{ backgroundColor: app.color }}
                >
                  <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                </div>
                <h3 className="text-sm sm:text-base font-semibold text-macos-gray-900 dark:text-white">
                  {app.name}
                </h3>
              </motion.button>
            );
          })}
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-macos-gray-500 dark:text-macos-gray-500 text-sm">
            WorkOs v1.0.0 by hxa.agency · Powered by Cloudflare Workers
          </p>
          <p className="text-macos-gray-400 dark:text-macos-gray-600 text-xs mt-2">
            Edge-first architecture · Global performance
          </p>
        </motion.div>
      </div>
    </div>
  );
}
